import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { animeArticles } from '@/mocks/anime';

const featuredAnime = animeArticles.filter((a) => a.featured);

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % featuredAnime.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + featuredAnime.length) % featuredAnime.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const current = featuredAnime[currentSlide];

  if (featuredAnime.length === 0) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={current.coverImage}
          alt={current.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2 py-0.5 text-xs font-medium bg-primary-500 text-background-50 rounded-full whitespace-nowrap">
                {current.categoryName}
              </span>
              <span className="text-xs text-background-50/80">{current.year}</span>
              <span className="text-xs text-background-50/80">|</span>
              <span className="text-xs text-accent-400 font-medium">{current.rating} 分</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
              {current.title}
            </h1>
            <p className="text-sm md:text-base text-background-50/80 leading-relaxed mb-6 line-clamp-2 md:line-clamp-3">
              {current.description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/article/${current.id}`}
                className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                立即查看
              </Link>
              <Link
                to={`/category/${current.category}`}
                className="px-6 py-2.5 border border-background-50/40 text-background-50 text-sm font-medium rounded-full hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多{current.categoryName}动漫
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-background-50/20 hover:bg-background-50/40 text-background-50 rounded-full transition-colors cursor-pointer whitespace-nowrap"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-background-50/20 hover:bg-background-50/40 text-background-50 rounded-full transition-colors cursor-pointer whitespace-nowrap"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {featuredAnime.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide
                ? 'w-6 bg-primary-500'
                : 'bg-background-50/50 hover:bg-background-50/80'
            }`}
            aria-label={`第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}